/**
 * Series: shared-tenant-content-boundary (#539, epic #530)
 *
 * Pure data. No `@/` aliases or JSX so Node build scripts (sitemap, llms,
 * prerender manifest) can import it directly through scripts/lib/series.js.
 *
 * Posts are resolved by id from the blog data at render time. `plannedTitle`
 * is only a fallback for parts that are not published yet (shown unlinked as
 * "upcoming"); a published post always supplies its own title and excerpt.
 */
export default {
  slug: 'shared-tenant-content-boundary',
  publishDate: '2027-01-01',
  title: 'Shared tenant content boundary: keeping two organisations apart in one Microsoft 365 tenant',
  shortTitle: 'Shared tenant content boundary',
  // 140 to 160 characters
  description:
    'A six-part design series on keeping SharePoint, Teams and OneDrive content apart in one shared Microsoft 365 tenant. Part 6 is held until a pilot has run.',
  intro:
    'Two organisations can share one Microsoft 365 tenant and still keep their content apart, but only if the boundary is designed rather than assumed. This series sets out a reviewed design for the content layer (SharePoint sites, Teams file sites and OneDrive for Business), using ordinary resource permissions, SharePoint restricted access control, Microsoft Entra entitlement management and dedicated joint workspaces. It is a design and a pilot plan, not a completed deployment.',
  audience:
    'Microsoft 365 administrators, IT managers, security and risk leads, and managed service providers who run one tenant for two or more organisations.',
  whoItsFor: [
    'IT managers who inherited a tenant shared by two organisations after a merger, partnership or outsourcing arrangement',
    'Microsoft 365 administrators who must show that content is separated, not just assume it',
    'Managed service providers who administer one tenant on behalf of more than one organisation',
    'Security and risk leads preparing the tenant for Microsoft 365 Copilot, where weak boundaries surface quickly',
  ],
  whatYouWillLearn: [
    'Why the content layer, not chat or email, is the boundary that matters in a shared tenant',
    'How SharePoint restricted access control works, and why site permissions alone are not enough',
    'How Microsoft Entra entitlement management can govern who gets access to joint workspaces, and for how long',
    'Where the edge cases sit: OneDrive for Business, Teams channel sites and applications',
    'How to design a pilot that tests the boundary before anyone relies on it',
  ],
  // Published parts, in reading order.
  parts: [
    { id: 142, plannedTitle: 'Two organisations, one Microsoft 365 tenant: where the content boundary sits' },
    { id: 143, plannedTitle: 'SharePoint restricted access control: why site permissions are not enough' },
    { id: 144, plannedTitle: 'Entra entitlement management for Teams and SharePoint access across two organisations' },
    { id: 145, plannedTitle: 'OneDrive, channel sites and applications: the content boundary edge cases' },
    { id: 146, plannedTitle: 'How to design a pilot that tests a shared-tenant content boundary' },
  ],
  // Part 6 is deliberately not published until a pilot has run and been verified.
  held: [
    {
      part: 6,
      title: 'What the pilot proved and what remains to operate',
      status: 'held pending a verified pilot',
    },
  ],
  totalParts: 6,
  methodology: 'Reviewed architecture and pilot plan, not a completed deployment. Anonymised: Org A and Org B.',
  methodologyDetail:
    'Every technical claim in this series is traced to Microsoft documentation in a source ledger, and each post states the date that documentation was checked. Where the documentation is silent, the series says so and labels the conclusion as our reading. No client, tenant or managed service provider is identified, and no pilot results are reported until a pilot has actually run.',
  sourceLedgerNote:
    'Each post carries a Sources section naming the Microsoft documentation pages behind its claims. Where documentation conflicts or is silent, the post labels the point as inference or recommendation rather than fact.',
  faqs: [
    {
      question: 'Can two organisations safely share one Microsoft 365 tenant?',
      answer:
        'They can, provided the boundary is designed and tested. A shared tenant does not separate content by default: a site, a team or a OneDrive is only as private as its permissions. This series covers a design that combines resource permissions, SharePoint restricted access control and governed access, plus a pilot plan to check it.',
    },
    {
      question: 'What is a content boundary in a shared Microsoft 365 tenant?',
      answer:
        'A content boundary is the set of controls that stops people in one organisation from reaching the files and sites that belong to the other. In this series it covers SharePoint sites, Teams file sites and OneDrive for Business, while Teams chat and Exchange email between the two organisations remain allowed.',
    },
    {
      question: 'Why not just use Information Barriers?',
      answer:
        'Information Barriers are not the default answer for this case. They cover collaboration in Teams, SharePoint and OneDrive, but not email, and they work in both directions. The series explains why this scenario does not lead with them, and labels that explanation as our reasoning.',
    },
    {
      question: 'What is SharePoint restricted access control?',
      answer:
        'Restricted access control is a SharePoint feature that limits a site to members of a named group, even where someone has been given access through other routes. It sits on top of ordinary permissions. Check your licensing, because it depends on SharePoint Advanced Management.',
    },
    {
      question: 'Has this design been deployed or tested?',
      answer:
        'No. This is a reviewed architecture and a pilot plan. Part 6, which would report what a pilot proved, is held until a pilot has run and its results have been verified. Until then the series makes no claims about observed results.',
    },
    {
      question: 'Who are Org A and Org B?',
      answer:
        'They are anonymised labels for the two organisations in the worked example. No tenant, client, sector or managed service provider is identified.',
    },
  ],
  cta: {
    heading: 'Is a shared-tenant content boundary right for your situation?',
    text: 'Talk to us about assessing whether this design suits your tenant, your licensing and your risk appetite.',
    primary: { label: 'Assess your SharePoint and Teams governance', href: '/sharepoint-teams-governance' },
    secondary: { label: 'Contact EDUC4TE', href: '/contact' },
  },
  // Date the series page content was last reviewed (ISO). Used for dateModified.
  lastReviewed: '2026-10-20',
};
